import { useCallback, useEffect, useRef, useState } from 'react';
import {
  widgetApi,
  type PublicQuestionSearchItem,
} from '../api/client';

export const PUBLIC_SEARCH_MIN_QUERY_LENGTH = 2;
export const PUBLIC_SEARCH_DEBOUNCE_MS = 300;
export const PUBLIC_SEARCH_RESULT_LIMIT = 8;
export const PUBLIC_SEARCH_STATUS = ['RESOLVED'] as const;
export const PUBLIC_SEARCH_MAX_AUTO_RETRIES = 1;
export const PUBLIC_SEARCH_BACKOFF_FALLBACK_MS = 5_000;
export const PUBLIC_SEARCH_BACKOFF_MIN_MS = 1_000;
export const PUBLIC_SEARCH_BACKOFF_MAX_MS = 60_000;

export type PublicQuestionSearchStatus =
  | 'idle'
  | 'loading'
  | 'results'
  | 'empty'
  | 'error'
  | 'rate-limited'
  | 'rate-limited-manual';

export interface PublicQuestionSearchState {
  status: PublicQuestionSearchStatus;
  questions: PublicQuestionSearchItem[];
  retryAtEpochMs: number | null;
}

export interface UsePublicQuestionSearchResult extends PublicQuestionSearchState {
  retry: () => void;
}

const IDLE_STATE: PublicQuestionSearchState = {
  status: 'idle',
  questions: [],
  retryAtEpochMs: null,
};

/** Normalize the Worker error detail to a bounded millisecond delay. */
export function normalizePublicSearchBackoffMs(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return PUBLIC_SEARCH_BACKOFF_FALLBACK_MS;
  }

  const milliseconds = Math.floor(value * 1_000);
  return Math.min(
    PUBLIC_SEARCH_BACKOFF_MAX_MS,
    Math.max(PUBLIC_SEARCH_BACKOFF_MIN_MS, milliseconds)
  );
}

function remainingPublicSearchBackoffMs(deadlineEpochMs: number): number {
  return Math.min(
    PUBLIC_SEARCH_BACKOFF_MAX_MS,
    Math.max(0, deadlineEpochMs - Date.now())
  );
}

/**
 * Debounced search controller for the Slice 2C Hub.
 *
 * A generation guard is retained in addition to AbortController because an
 * intermediary or test double may resolve after abort. Every outcome, including
 * `finally`, is generation-checked so stale work cannot paint or clear loading.
 */
export function usePublicQuestionSearch(
  query: string,
  enabled: boolean = true
): UsePublicQuestionSearchResult {
  const [state, setState] = useState<PublicQuestionSearchState>(IDLE_STATE);
  const [retryRevision, setRetryRevision] = useState(0);
  const generationRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const backoffUntilRef = useRef(0);

  const retry = useCallback(() => setRetryRevision((value) => value + 1), []);

  useEffect(() => {
    const generation = ++generationRef.current;
    const normalizedQuery = query.trim();

    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    controllerRef.current?.abort();
    controllerRef.current = null;

    if (!enabled || normalizedQuery.length < PUBLIC_SEARCH_MIN_QUERY_LENGTH) {
      setState(IDLE_STATE);
      return () => {
        generationRef.current += 1;
      };
    }

    const activeBackoffMs = remainingPublicSearchBackoffMs(backoffUntilRef.current);
    setState(
      activeBackoffMs > 0
        ? {
            status: 'rate-limited',
            questions: [],
            retryAtEpochMs: backoffUntilRef.current,
          }
        : { status: 'loading', questions: [], retryAtEpochMs: null }
    );

    const schedule = (callback: () => void, delayMs: number): void => {
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        callback();
      }, delayMs);
    };

    const execute = async (attempt: number): Promise<void> => {
      if (generationRef.current !== generation) return;

      const remainingBackoffMs = remainingPublicSearchBackoffMs(backoffUntilRef.current);
      if (remainingBackoffMs > 0) {
        setState({
          status: 'rate-limited',
          questions: [],
          retryAtEpochMs: backoffUntilRef.current,
        });
        schedule(() => {
          if (generationRef.current !== generation) return;
          // The timer is the monotonic authority for elapsed wait. Clearing the
          // wall-clock deadline also makes this resilient to a storefront that
          // freezes or rewinds Date (and to the deterministic browser harness).
          backoffUntilRef.current = 0;
          void execute(attempt);
        }, remainingBackoffMs);
        return;
      }

      const controller = new AbortController();
      controllerRef.current = controller;
      setState({ status: 'loading', questions: [], retryAtEpochMs: null });

      try {
        const response = await widgetApi.questions.search(normalizedQuery, {
          limit: PUBLIC_SEARCH_RESULT_LIMIT,
          status: PUBLIC_SEARCH_STATUS,
          signal: controller.signal,
        });

        if (controller.signal.aborted || generationRef.current !== generation) return;

        if (response.success) {
          backoffUntilRef.current = 0;
          // RESOLVED is the strongest existing server-side solved-case proxy,
          // but a documented repair window can expose a zero-reply anomaly.
          // Never paint that anomaly as a solved Hub result.
          const answerBearingQuestions = response.data.questions.filter(
            (question) => question.status === 'RESOLVED' && question.replyCount > 0
          );
          setState({
            status: answerBearingQuestions.length > 0 ? 'results' : 'empty',
            questions: answerBearingQuestions,
            retryAtEpochMs: null,
          });
          return;
        }

        if (response.error.code === 'RATE_LIMITED') {
          const backoffMs = normalizePublicSearchBackoffMs(
            response.error.details?.retryAfter
          );
          backoffUntilRef.current = Date.now() + backoffMs;
          const willRetryAutomatically = attempt < PUBLIC_SEARCH_MAX_AUTO_RETRIES;
          setState({
            status: willRetryAutomatically ? 'rate-limited' : 'rate-limited-manual',
            questions: [],
            retryAtEpochMs: backoffUntilRef.current,
          });

          if (willRetryAutomatically) {
            schedule(() => {
              if (generationRef.current !== generation) return;
              backoffUntilRef.current = 0;
              void execute(attempt + 1);
            }, backoffMs);
          } else {
            // Expiry only enables the explicit control; it must never issue a
            // third request on its own. Keep the state machine timer-owned so
            // the UI can truthfully disable the action until the server's
            // bounded retry window has elapsed.
            schedule(() => {
              if (generationRef.current !== generation) return;
              backoffUntilRef.current = 0;
              setState({
                status: 'rate-limited-manual',
                questions: [],
                retryAtEpochMs: null,
              });
            }, backoffMs);
          }
          return;
        }

        setState({ status: 'error', questions: [], retryAtEpochMs: null });
      } catch {
        if (!controller.signal.aborted && generationRef.current === generation) {
          setState({ status: 'error', questions: [], retryAtEpochMs: null });
        }
      } finally {
        if (controllerRef.current === controller) controllerRef.current = null;
      }
    };

    const initialDelayMs = Math.max(
      PUBLIC_SEARCH_DEBOUNCE_MS,
      activeBackoffMs
    );
    schedule(() => void execute(0), initialDelayMs);

    return () => {
      generationRef.current += 1;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      controllerRef.current?.abort();
      controllerRef.current = null;
    };
  }, [enabled, query, retryRevision]);

  return { ...state, retry };
}
